import Vue from 'vue';

const DEFAULT_PLAYER_OPTIONS = {
  loop: false,
  volume: 1,
};

let soundController = {};

function fetchFile(src) {
  return fetch(src)
    .then(res => {
      if (!res.ok) throw new Error('Bad response from server');
      if (res.blob) return res.blob();

      throw new Error('Wrong response type');
    })
    .catch(err => {
      // eslint-disable-next-line no-console
      console.error(`Could not fetch sound file ${src}`, err);
    });
}

// http://diveintohtml5.info/everything.html#audio-aac
const supportsAac = () => {
  const a = document.createElement('audio');
  return !!(a.canPlayType && a.canPlayType('audio/mp4; codecs="mp4a.40.2"').replace(/no/, ''));
};

function initializePlayer() {
  const player = new Audio();

  // iOS audio fix
  // https://stackoverflow.com/a/57547943
  return new Promise(resolve => {
    const startPlayer = () => {
      // Unlock web audio api for iOS by playing an empty buffer / nothing
      // https://paulbakaus.com/tutorials/html5/web-audio-on-ios/
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioContext();

        // create empty buffer
        const buffer = audioCtx.createBuffer(1, 1, 22050);
        // https://stackoverflow.com/a/60215835
        const blob = new Blob([buffer], { type: 'audio/wav' });

        player.src = URL.createObjectURL(blob);
      } catch {
        // INFO: if could not create empty sound, just play nothing.
        // sadly this might produce cracking sounds
      }

      // play the empty file
      player.play();
      resolve(player);
    };

    // onClick of first interaction on page before we need the sounds
    document.body.addEventListener('click', startPlayer, { capture: true, once: true });
  });
}

// soundConfig enables us to define sounds via:
// - strings (src) or
// - object (Audio player attributes)
function formatSoundConfig(config) {
  if (!config) return [];

  return Object.entries(config)
    .filter(config => ['string', 'object'].includes(typeof config))
    .map(([id, config]) => ({
      [id]: Object.keys(config).includes('source') ? config : { source: config },
    }))
    .reduce((config, entry) => Object.assign(config, entry), {});
}

export function initSoundController(playersConfig, soundConfig) {
  if (!playersConfig || !soundConfig) {
    // eslint-disable-next-line no-console
    // console.log('sound controller needs config');
    return;
  }

  soundController = new Vue({
    data() {
      return {
        soundEnabled: true,
        startPlayback: false,

        supportedAudioType: supportsAac() ? 'aac' : 'mp3',

        sounds: formatSoundConfig(soundConfig),
        loadedSounds: {},
        players: [],
      };
    },

    watch: {
      sounds: {
        immediate: true,
        handler(sounds) {
          this.loadedSounds = Object.keys(sounds).reduce((loadedSounds, id) => {
            const status = this.loadedSounds[id];
            return Object.assign(loadedSounds, {
              [id]: {
                promise: status?.promise || undefined,
                isLoaded: status?.isLoaded ? status.isLoaded : undefined, // can be false
              },
            });
          }, {});
        },
      },

      soundEnabled(soundEnabled) {
        if (!soundEnabled) this.pauseAllPlayers();
      },
    },

    created() {
      // create unussed audiocontext for safari to improve performance…
      // https://stackoverflow.com/a/54119854
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      new AudioContext();

      // we start preloading all the audio files with html audio
      Object.entries(this.sounds).forEach(([id, { source }]) => {
        this.loadedSounds[id].promise = fetchFile(source[this.supportedAudioType]);
        this.loadedSounds[id].promise.then(() => {
          this.loadedSounds[id].isLoaded = true;
        });
      });

      Promise.all(Object.values(this.loadedSounds).map(({ promise }) => promise)).then(() => {
        // eslint-disable-next-line no-console
        // console.log('all sounds loaded');
      });

      // playersConfig enables us to define audio players
      Object.keys(playersConfig).forEach(playerId => {
        this.players[playerId] = initializePlayer(playerId);
      });

      this.initPauseOnVisabilityChange();
    },

    methods: {
      initPauseOnVisabilityChange() {
        // this follows the example from
        // https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API

        // Set the name of the hidden property and the change event for visibility
        let hidden;
        let visibilityChange;
        if (typeof document.hidden !== 'undefined') {
          // Opera 12.10 and Firefox 18 and later support
          hidden = 'hidden';
          visibilityChange = 'visibilitychange';
        } else if (typeof document.msHidden !== 'undefined') {
          hidden = 'msHidden';
          visibilityChange = 'msvisibilitychange';
        } else if (typeof document.webkitHidden !== 'undefined') {
          hidden = 'webkitHidden';
          visibilityChange = 'webkitvisibilitychange';
        }

        const handleVisibilityChange = () => {
          if (document[hidden]) {
            // If the page is hidden, pause audio
            this.pauseAllPlayers();
          } else {
            // if the page is shown, play audio
            this.startAllPlayers();
          }
        };

        // Requires a browser,that supports addEventListener and the Page Visibility API
        if (typeof document.addEventListener === 'undefined' || hidden === undefined) return;

        // Handle page visibility change
        document.addEventListener(visibilityChange, handleVisibilityChange, false);

        this.$once('hook:beforeDestroy', () => {
          document.removeEventListener(visibilityChange, handleVisibilityChange);
        });
      },

      /**
       * Play a sound with a player
       * @param {string} playerId
       * @param {string} soundId
       * @param {Object} options
       * @param {string} options.continue - The name of the employee.
       */
      async playSound(playerId, soundId, options) {
        // eslint-disable-next-line no-console
        // console.log('playSound: ', playerId, soundId);
        if (
          !this.soundEnabled ||
          !this.startPlayback ||
          !this.players[playerId] ||
          !this.sounds ||
          !this.sounds[soundId]
        ) {
          // eslint-disable-next-line no-console
          console.log(`Could not play sound ${soundId}`);
          return Promise.reject();
        }

        // wait for sound beeing loaded
        // console.log('is loaded', this.loadedSounds[soundId].isLoaded);
        // await this.loadedSounds[soundId];

        const player = await this.players[playerId]; // resolves immediately
        const { source, ...otherOptions } = this.sounds[soundId];
        const src = source[this.supportedAudioType];

        const soundEnded = new Promise(resolve => {
          player.addEventListener(
            'ended',
            () => {
              // player.src = '';
              resolve();
            },
            { once: true }
          );
        });

        if (options && options.continue === true && player.src && player.src.includes(src)) {
          // continue playback if file currently already playing
          player.play();
          return soundEnded;
        }

        // pause existing audio (has no effect if not playing)
        player.pause();

        // reset player and assign new values
        Object.assign(player, { ...DEFAULT_PLAYER_OPTIONS, ...otherOptions });
        player.src = src;

        const isPlaying = player.play();

        return isPlaying !== undefined ? Promise.all([isPlaying, soundEnded]) : soundEnded;
      },

      startAllPlayers() {
        if (!this.soundEnabled || !this.startPlayback) return;
        Object.values(this.players).forEach(player =>
          player.then(player => !player.ended && player.play())
        );
      },

      pauseAllPlayers() {
        Object.values(this.players).forEach(player => player.then(player => player.pause()));
      },

      pausePlayer(playerId) {
        const player = this.players[playerId];
        if (!player) return;

        this.players[playerId].then(player => player.pause());
      },

      setSoundEnabled(soundEnabled) {
        this.soundEnabled = soundEnabled;
      },

      setStartPlayback(startPlayback) {
        this.startPlayback = startPlayback;
      },

      destroy() {
        // TODO: remove player
        // TODO: Fix issue with this function call: "Error in event handler for "hook:beforeDestroy": "TypeError: fn is undefined""
        // this.$destroy();
      },
    },
  });
  return soundController;
}

const createPublicMethod = methodName => (...args) => soundController[methodName](...args);

export { soundController };

export const setSoundEnabled = createPublicMethod('setSoundEnabled');
export const setStartPlayback = createPublicMethod('setStartPlayback');
export const destroySoundController = createPublicMethod('destroy');
export const playSound = createPublicMethod('playSound');
export const pausePlayer = createPublicMethod('pausePlayer');
